import { useEffect, useMemo, useState } from 'react';
import { Button, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { RequirementFieldOption, RequirementFieldWithChildren } from '../data-collection.dto';
import { FieldValueMap, evaluateListSize, isFieldRequired, isFieldVisible } from './data-collection.helpers';
import { RequirementFieldInput } from './RequirementFieldInput';
import { SelectFieldInput } from './SelectFieldInput';

export type ListEntry = Record<string, string | number | boolean | string[] | null>;

interface Props {
    field: RequirementFieldWithChildren;
    entries: ListEntry[];
    dynamicOptions: Record<string, RequirementFieldOption[]>;
    /**
     * Top-level form values keyed by field code. Merged with each row's own
     * values when evaluating sub-field conditions, so a sub-field can reference
     * a top-level sibling (e.g. `{"var":"meter_count"}` from inside a row).
     * Also feeds the LIST's `listSize` evaluator.
     */
    parentValues: FieldValueMap;
    disabled?: boolean;
    disabledReason?: string | null;
    onChange: (next: ListEntry[]) => void;
}

/**
 * Controlled repeating-group editor. The parent form owns the value; this
 * component is value/onChange only. Auto-numbered entry header uses the
 * parent field's `name` as the singular noun; the add-button label is derived
 * from the same value.
 *
 * Two modes:
 *   - Free-add (`field.listSize == null`) — user adds/removes rows freely.
 *   - Count-driven (`field.listSize != null`) — the JSON-logic expression
 *     evaluated against `parentValues` fixes the row count. Add/remove buttons
 *     are hidden; the entries array is auto-synced (pad with blanks or
 *     truncate from the end) to match the evaluated count.
 */
export function ListFieldRenderer({
    field,
    entries,
    dynamicOptions,
    parentValues,
    disabled,
    disabledReason,
    onChange,
}: Props) {
    const { t, i18n } = useTranslation();

    // Which entry's tab is open. Clamped to the live entry count at render.
    const [activeIndex, setActiveIndex] = useState(0);

    const subFields = field.subFields;
    const targetCount = useMemo(
        () => (field.listSize == null ? null : evaluateListSize(field.listSize, parentValues)),
        [field.listSize, parentValues],
    );
    const countDriven = targetCount != null;
    const showAddWarning = !countDriven && field.dataCriticality === 'mandatory' && entries.length === 0;

    // Singular noun for "{{entry}}" interpolation. Lowercasing reads naturally
    // in English mid-sentence ("add another meter") but breaks German, where
    // nouns are always capitalized ("ein Zähler", not "ein zähler") — so keep
    // the original casing for German.
    const entryNoun = i18n.language?.startsWith('de') ? field.name : field.name.toLowerCase();

    // Sync entries.length to targetCount when count-driven. Runs after render
    // so we don't mutate state during render; convergence is one extra render.
    useEffect(() => {
        if (targetCount == null) return;
        if (entries.length === targetCount) return;
        if (entries.length < targetCount) {
            const pad = targetCount - entries.length;
            const blanks = Array.from({ length: pad }, () => blankEntry(subFields));
            onChange([...entries, ...blanks]);
        } else {
            // Truncation drops the trailing rows; the user lowered the count
            // and we silently honor it. No confirm-prompt for v1.
            onChange(entries.slice(0, targetCount));
        }
        // Depending only on lengths avoids loops when row contents change.
    }, [targetCount, entries.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Display the right number of rows immediately, even before the effect
    // has synced the persisted state — avoids a flash of the wrong count.
    const displayEntries = useMemo<ListEntry[]>(() => {
        if (targetCount == null) return entries;
        if (entries.length === targetCount) return entries;
        if (entries.length < targetCount) {
            return [...entries, ...Array.from({ length: targetCount - entries.length }, () => blankEntry(subFields))];
        }
        return entries.slice(0, targetCount);
    }, [entries, targetCount, subFields]);

    const updateSubField = (entryIndex: number, code: string, next: ListEntry[string]) => {
        // Operate on the persisted `entries` if it already matches displayEntries;
        // otherwise build from displayEntries (the effect will reconcile next tick).
        const source = entries.length === displayEntries.length ? entries : displayEntries;
        onChange(source.map((e, i) => (i === entryIndex ? { ...e, [code]: next } : e)));
    };

    const handleAdd = () => {
        onChange([...entries, blankEntry(subFields)]);
        setActiveIndex(entries.length); // focus the newly added tab
    };

    const handleRemove = (index: number) => {
        onChange(entries.filter((_, i) => i !== index));
        setActiveIndex((cur) => (cur >= index && cur > 0 ? cur - 1 : cur));
    };

    // Keep the open tab within range as the entry count changes.
    const safeIndex = displayEntries.length === 0 ? 0 : Math.min(activeIndex, displayEntries.length - 1);

    // Sub-fields for a single entry — rendered for the active tab only.
    const renderEntryFields = (entry: ListEntry, index: number) => {
        const entryValues: FieldValueMap = { ...parentValues, ...(entry as FieldValueMap) };
        return subFields
            .filter((sf) => isFieldVisible(sf, entryValues))
            .map((subField) => {
                const required = isFieldRequired(subField, entryValues);
                const value = entry[subField.code] ?? null;

                if (subField.fieldType === 'select') {
                    const options =
                        subField.optionSource === 'static'
                            ? (subField.options ?? [])
                            : (dynamicOptions[subField.optionSource ?? ''] ?? []);
                    const selectValue = subField.multiple
                        ? Array.isArray(value)
                            ? value
                            : []
                        : typeof value === 'string'
                          ? value
                          : null;
                    // A building belongs to a single meter: grey out (disable) buildings
                    // already chosen in other entries so they can't be assigned here too.
                    const disabledValues =
                        subField.optionSource === 'buildings'
                            ? valuesUsedByOtherEntries(displayEntries, index, subField.code)
                            : undefined;
                    return (
                        <SelectFieldInput
                            key={subField.id}
                            field={subField}
                            value={selectValue}
                            options={options}
                            required={required}
                            disabled={disabled}
                            disabledValues={disabledValues}
                            onChange={(next) => updateSubField(index, subField.code, next as ListEntry[string])}
                        />
                    );
                }

                return (
                    <RequirementFieldInput
                        key={subField.id}
                        field={subField}
                        value={(value as string | number | boolean | null | undefined) ?? null}
                        required={required}
                        disabled={disabled}
                        onChange={(next) => updateSubField(index, subField.code, next as ListEntry[string])}
                    />
                );
            });
    };

    return (
        <Stack spacing={1}>
            {disabled && disabledReason && (
                <Typography variant="caption" color="text.disabled">
                    {disabledReason}
                </Typography>
            )}
            {displayEntries.length > 0 && (
                <>
                    <Tabs
                        value={safeIndex}
                        onChange={(_, next: number) => setActiveIndex(next)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0, width: 0 } }}
                    >
                        {displayEntries.map((_, index) => (
                            <Tab key={index} value={index} label={entryName(field.name, index)} />
                        ))}
                    </Tabs>
                    <Stack
                        spacing={1}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                            bgcolor: 'action.hover',
                        }}
                    >
                        {!countDriven && (
                            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemove(safeIndex)}
                                    aria-label={t('Remove entry')}
                                    disabled={disabled}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            </Stack>
                        )}
                        {renderEntryFields(displayEntries[safeIndex], safeIndex)}
                    </Stack>
                </>
            )}
            {!countDriven && (
                <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAdd}
                    disabled={disabled}
                    color={showAddWarning ? 'warning' : 'primary'}
                    variant={showAddWarning ? 'outlined' : 'text'}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    {t('Add another {{entry}}', { entry: entryNoun })}
                </Button>
            )}
        </Stack>
    );
}

/** Union of a select sub-field's values across all entries except `exceptIndex`. */
function valuesUsedByOtherEntries(entries: ListEntry[], exceptIndex: number, code: string): Set<string> {
    const used = new Set<string>();
    entries.forEach((entry, i) => {
        if (i === exceptIndex) return;
        const v = entry[code];
        if (Array.isArray(v)) {
            for (const id of v) used.add(String(id));
        } else if (typeof v === 'string' && v) {
            used.add(v);
        }
    });
    return used;
}

function entryName(parentName: string, index: number): string {
    return `${parentName} ${index + 1}`;
}

function blankEntry(subFields: RequirementFieldWithChildren[]): ListEntry {
    return Object.fromEntries(subFields.map((sf) => [sf.code, defaultForType(sf.fieldType, sf.multiple)]));
}

function defaultForType(type: string, multiple: boolean): ListEntry[string] {
    if (type === 'boolean') return false;
    if (type === 'number') return null;
    if (type === 'select') return multiple ? [] : null;
    return '';
}
